import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import Header from "../components/ui/Header";
import Icon from "../components/AppIcon";
import { Link } from "react-router-dom";
import Button from "../components/ui/Button";

const domains = [
  { 
    name: "Robotics", 
    image: "/assets/images/domains/robotics.png",
    tagline: "Build robots from scratch to automation."
  },
  { 
    name: "Python", 
    image: "/assets/images/domains/python.png",
    tagline: "Master coding with real‑world apps."
  },
  { 
    name: "Data Science", 
    image: "/assets/images/domains/data-science.png",
    tagline: "Turn raw data into business insights."
  },
  { 
    name: "Computer Vision", 
    image: "/assets/images/domains/computer-vision.png",
    tagline: "Teach machines to “see” the world."
  },
  { 
    name: "AI / ML", 
    image: "/assets/images/domains/ai-ml.png",
    tagline: "Build intelligent, learning systems."
  },
  { 
    name: "Gen AI", 
    image: "/assets/images/domains/gen-ai.png",
    tagline: "Create with the power of AI."
  },
  { 
    name: "Research Projects", 
    image: "/assets/images/domains/research.png",
    tagline: "Work on breakthrough technologies."
  },
  { 
    name: "Competitions", 
    image: "/assets/images/domains/competitions.png",
    tagline: "Test your skills and win accolades."
  }
];

// ✅ SEO-friendly projects for all domains
const seoProjects = {
  "Robotics": [
    {
      title: "Line Follower Bot",
      description: "Self-learning robot with obstacle avoidance and path optimization."
    },
    {
      title: " Pick and Place Arm",
      description: "Custom-built drone for last-mile package delivery."
    },
    {
      title: "IoT Delivery Drone",
      description: "Robotic arm for precision manufacturing tasks."
    }
  ],
  "Python": [
    {
      title: "Number Guessing Game",
      description: "Lightweight Python API for managing product catalogs and orders."
    },
    {
      title: "CLI To-Do App",
      description: "Python script that converts analytics data into formatted PDF reports."
    },
    {
      title: "Flask E-commerce API",
      description: "Command-line weather app using OpenWeatherMap API."
    }
  ],
  "Data Science": [
    {
      title: "CSV Cleaner",
      description: "Predict customer churn using logistic regression and random forests."
    },
    {
      title: "Sales Forecast Dashboard",
      description: "Time series-based forecasting for retail sales trends."
    },
    {
      title: "Churn Prediction Model",
      description: "Classify product reviews as positive or negative using NLP techniques."
    }
  ],
  "Computer Vision": [
    {
      title: "Face Mask Detector",
      description: "Detects whether a person is wearing a mask in real-time."
    },
    {
      title: "License Plate Recognition",
      description: "Extracts and reads license plate numbers from car images."
    },
    {
      title: "Human Pose Tracking",
      description: "Control system actions based on hand gestures via webcam."
    }
  ],
  "AI / ML": [
    {
      title: "STock Price Predictor",
      description: "AI-powered chatbot trained on FAQs to automate support queries."
    },
    {
      title: "AI Chatbot",
      description: "Predicts stock prices using LSTM neural networks."
    },
    {
      title: "Recommendation Engine",
      description: "Classifies songs into genres using audio feature extraction."
    }
  ],
  "Gen AI": [
    {
      title: "AI Quiz Maker",
      description: "Generates SEO-friendly blog posts using GPT-based models."
    },
    {
      title: "Resume Optimizer",
      description: "Transforms images into famous art styles using neural style transfer."
    },
    {
      title: "AImage Style Transfer",
      description: "Optimizes resumes for better ATS (Applicant Tracking System) scores."
    }
  ],
  "Research Projects": [
    {
      title: "Open Data Analysis",
      description: "Simulates quantum circuits for research and education."
    },
    {
      title: "BCI Study",
      description: "Research on controlling devices directly using brain signals."
    },
    {
      title: "Climate Change Prediction",
      description: "Analyzing global temperature data to predict climate trends."
    }
  ],
  "Competitions": [
    {
      title: "AI Hackathon: Healthcare",
      description: "Built a model for early detection of lung diseases."
    },
    {
      title: "Robotics Challenge: Obstacle Course",
      description: "Autonomous robot successfully completed complex obstacle navigation."
    },
    {
      title: "Kaggle Solutions",
      description: "Ranked top 10% in predicting housing prices dataset."
    }
  ]
};

const IndividualLearningNew = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Individual Learning - Dream Coders Club</title>
        <meta
          name="description"
          content="Explore hands-on learning in Robotics, AI/ML, Python, and more. Build SEO-friendly projects and gain industry-ready skills."
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            
            {/* Intro Section */}
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-foreground mb-4">
                Learn. Build. Showcase.
              </h1>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Gain hands-on experience in cutting-edge domains like AI, Robotics, and Gen AI. 
                Build SEO-optimized projects and showcase them to the world.
              </p>
            </div>

            {/* Domains Grid WITH Projects Inside */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {domains.map((domain, index) => (
                <div key={index} className="holographic-card p-6 text-left">

                  {/* Domain Image */}
                  <img
                    src={domain.image}
                    alt={domain.name}
                    className="w-full h-40 mx-auto mb-4 rounded-md object-cover"
                  />

                  {/* Domain Name */}
                  <h3 className="text-lg font-bold text-foreground mb-2">
                    {domain.name}
                  </h3>

                  {/* 1-Liner Tagline */}
                  <p className="text-sm text-muted-foreground mb-4">
                    {domain.tagline}
                  </p>

                  {/* Projects Featured */}
                  <div className="text-left">
                    <h4 className="text-sm font-semibold text-accent mb-2">
                      Projects Featured:
                    </h4>
                    <ul className="space-y-1">
                      {seoProjects[domain.name]?.map((project, idx) => (
                        <li key={idx} className="text-xs text-muted-foreground">
                          {idx + 1}. {project.title}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* ✅ Enroll Now Section */}
          <div className="text-center mt-16 mb-20">
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-6">
              Ready to take your skills to the next level?  
              Enroll in our hands-on programs and start building real-world projects today!
            </p>
            <Link to="/contact-support">
              <Button
                variant="default"
                size="sm"
                className="morphing-cta text-white font-semibold"
              >
                Enroll Now
              </Button>
            </Link>
          </div>

        </main>
          {/* Footer */}
          <footer className="bg-primary border-t border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid md:grid-cols-4 gap-8">
              {/* Company Info */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-accent to-cyber-purple flex items-center justify-center">
                    <span className="text-white font-bold text-sm">DC</span>
                  </div>
                  <span className="text-lg font-bold text-foreground">Dream Coders Club</span>
                </div>
                <p className="text-muted-foreground text-sm">
                  Transforming education through AI and Robotics innovation. Building tomorrow's workforce today.
                </p>
              </div>

              {/* Solutions */}
              <div>
                <h3 className="text-foreground font-semibold mb-4">Follow Us</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li><a href="https://www.linkedin.com/company/dreamcodersclub" target = "_blank" className="hover:text-accent transition-colors">LinkedIn</a></li>
                  {/* <li><a href="#" className="hover:text-accent transition-colors">Corporate Training</a></li>
                  <li><a href="#" className="hover:text-accent transition-colors">Government Programs</a></li>
                  <li><a href="#" className="hover:text-accent transition-colors">Custom Solutions</a></li> */}
                </ul>
              </div>

              {/* Resources */}
              <div>
                <h3 className="text-foreground font-semibold mb-4">Free Resources</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li><a href="#" className="hover:text-accent transition-colors">Python Beginner's Guide</a></li>
                  {/* <li><a href="#" className="hover:text-accent transition-colors">Case Studies</a></li>
                  <li><a href="#" className="hover:text-accent transition-colors">ROI Calculator</a></li>
                  <li><a href="#" className="hover:text-accent transition-colors">Industry Reports</a></li> */}
                </ul>
              </div>

              {/* Contact */}
              <div>
                <h3 className="text-foreground font-semibold mb-4">Contact</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>dreamcodersclub99@gmail.com</li>
                  <li>+91 99208 85753</li>
                </ul>
              </div>
            </div>

            <div className="border-t border-border mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
              <p className="text-sm text-muted-foreground">
                © {new Date().getFullYear()} Clusterize Learning LLP. All rights reserved.
              </p>
              {/* <div className="flex space-x-6 mt-4 md:mt-0">
                <a href="#" className="text-sm text-muted-foreground hover:text-accent transition-colors">Privacy Policy</a>
                <a href="#" className="text-sm text-muted-foreground hover:text-accent transition-colors">Terms of Service</a>
                <a href="#" className="text-sm text-muted-foreground hover:text-accent transition-colors">Security</a>
              </div> */}
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default IndividualLearningNew;
